import React from "react";

// Reusable vendor identity assets — logos + colors + name — defined ONCE here and used wherever a
// system (SUT) or emulator model is shown (Runs & Storage, and available to Run-Experiment).
// Map a run's `system` or `emulator_model` to a vendor, then render <VendorBadge vendor=... />.
//
// The logos are inline SVG so they need no network fetch (CSP-safe) and scale crisply. To swap in an
// official brand SVG later, replace that vendor's `Logo` component below — nothing else changes.

const OpenAILogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#0d0d0d" aria-hidden="true">
    <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7495-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997z" />
  </svg>
);

// Anthropic / Claude sunburst — a radial burst in Anthropic clay.
const AnthropicLogo = ({ size = 18 }) => {
  const rays = Array.from({ length: 12 }, (_, i) => {
    const a = (i * Math.PI) / 6;
    const c = Math.cos(a), s = Math.sin(a);
    return { x1: 12 + 3.4 * c, y1: 12 + 3.4 * s, x2: 12 + 10 * c, y2: 12 + 10 * s };
  });
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <g stroke="#d97757" strokeWidth="2.2" strokeLinecap="round">
        {rays.map((r, i) => <line key={i} x1={r.x1} y1={r.y1} x2={r.x2} y2={r.y2} />)}
      </g>
    </svg>
  );
};

const GoogleLogo = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// xAI (Grok): rendered as the "xAI" WORDMARK (black bold italic sans-serif), not an icon. It spells the
// brand, so it stands in for both the logo and the name — see the `wordmark` handling in VendorBadge
// (no square icon box, no duplicate text label). CSP-safe (system fonts, no assets).
const XaiLogo = ({ size = 18 }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", lineHeight: 1, whiteSpace: "nowrap",
    color: "#0d0d0d", fontStyle: "italic", fontWeight: 800, letterSpacing: "-0.01em",
    fontSize: Math.round(size * 0.88),
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  }}>xAI</span>
);

// Mari mark: a bare "M" in Newsreader (serif), hekavor purple — no disc. (The full Hekavor SVG mark
// didn't read well at badge size.)
const MariLogo = ({ size = 18 }) => (
  <span style={{
    width: size, height: size, display: "inline-flex", alignItems: "center", justifyContent: "center",
    color: "#511ccd", fontFamily: "var(--serif)", fontWeight: 700, fontSize: Math.round(size * 0.95), lineHeight: 1,
  }}>M</span>
);

export const VENDORS = {
  mari: { name: "Mari", color: "#511ccd", Logo: MariLogo },
  openai: { name: "OpenAI", color: "#10a37f", Logo: OpenAILogo },
  anthropic: { name: "Anthropic", color: "#d97757", Logo: AnthropicLogo },
  google: { name: "Google", color: "#4285f4", Logo: GoogleLogo },
  xai: { name: "xAI", color: "#0d0d0d", Logo: XaiLogo, wordmark: true },
};

// A SUT system string -> vendor key. "mari" -> mari; "history:openai" -> openai (drop the prefix).
export function vendorOfSystem(system) {
  if (!system) return null;
  if (system === "mari") return "mari";
  if (system.startsWith("history:")) return system.slice("history:".length);
  return system;
}

// An emulator model id -> vendor key (by slug prefix).
export function vendorOfModel(model) {
  const m = (model || "").toLowerCase();
  if (m.startsWith("claude")) return "anthropic";
  if (m.startsWith("gpt") || /^o[134]/.test(m)) return "openai";
  if (m.startsWith("gemini")) return "google";
  if (m.startsWith("grok")) return "xai";
  return null;
}

// Color-coded logo + vendor name in default ink (the logo carries the color, so the name stays neutral).
// Pass label={false} for the icon only.
export default function VendorBadge({ vendor, label = true, size = 18 }) {
  const v = VENDORS[vendor];
  if (!v) return <span className="muted">{vendor || "—"}</span>;
  const Logo = v.Logo;
  // Wordmark vendors (e.g. xAI): the Logo already spells the brand, so it IS both the icon and the name —
  // render it alone (no fixed-width icon box, no duplicate text label), whether or not `label` is set.
  if (v.wordmark) {
    return <span className="vbadge" title={v.name}><Logo size={size} /></span>;
  }
  return (
    <span className="vbadge" title={v.name}>
      <span className="vbadge-logo" style={{ width: size, height: size }}><Logo size={size} /></span>
      {label && <span className="vbadge-name">{v.name}</span>}
    </span>
  );
}
